import React from 'react';
import {useNavigate} from 'react-router-dom';
import {inAnteprima, skinCorrente} from '../../skin';
import {registra, cercaInvito} from '../../services/registrazioneApi';
import Nota from '../Vetrina/Nota';
import { ritornoAutomaticoTelegram, avviaRitornoTelegram, ritornoTelegram, confermaConTelegram, mantieProntoTelegram, preparaTelegram } from '../../services/telegramAccesso';
import './Registrazione.css';
import i18next from 'i18next';
import {frase} from '../../frase';
import {lingua} from '../../preferenze';

/**
 * REGISTRATI: il cliente si apre un conto da solo.
 *
 * COSA FA: mostra SOLO i campi e i canali di conferma che la skin ha acceso
 * (skin.registrazione, da tenants by-domain): nome utente, password e
 * termini sempre; email, telefono, paese, data di nascita, codici secondo
 * la skin. Manda la richiesta con il dominio; se va, porta a «Conto in
 * attesa» con il canale scelto. Gli errori arrivano col codice e il
 * messaggio del servizio («Errore 409 - nome utente gia' usato»).
 *
 * COSA NON FA: non decide cosa e' obbligatorio (lo dice la skin, lo
 * ricontrolla user-management); non verifica l'identita' (KYC, non ancora
 * collegato); non registra se la skin ha la registrazione chiusa: lo scrive.
 *
 * NEL PANNELLO DAL BASSO (skin Nova, telefono) O NELLA FINESTRA (FinestraRegistrazione):
 * `nelFoglio` toglie il titolo (lo ha il pannello) e «Accedi» chiama `onAccedi`,
 * che apre la finestra di accesso invece di cambiare pagina. `onChiudi`, quando
 * c'e', chiude la finestra (chi e' gia' dentro, conto appena aperto) invece di
 * cambiare pagina. `ricerca` e' la query dell'indirizzo da cui si e' arrivati
 * (?code=… del ritorno da Telegram, ?invito=…), quando l'indirizzo e' gia'
 * cambiato: senza, si legge quella della pagina. Il resto e' identico.
 *
 * DAL BACKOFFICE (Skin › Registrazione): registrazione aperta/chiusa, canali,
 * campi obbligatori/facoltativi/nascosti, durata del gettone.
 */
/* A chi gioca, quando la registrazione dal sito non si puo' fare: niente codici ne' backoffice. */
export const CHIUSA_PER_ORA = frase('La registrazione dal sito non è ancora disponibile. Per aprire un conto scrivi all\'assistenza.');
const NOMI_CANALE = {email: frase('Email'), telegram: frase('Telegram'), sms: frase('SMS')};

const CAMPI = [
  {chiave: 'email', get nome() { return i18next.t('Email'); }, tipo: 'email', auto: 'email', get aiuto() { return i18next.t('tu@esempio.com'); }},
  {chiave: 'telefono', get nome() { return i18next.t('Telefono'); }, tipo: 'tel', auto: 'tel', get aiuto() { return i18next.t('+39 333 1234567, con il prefisso'); }},
  {chiave: 'paese', get nome() { return i18next.t('Paese di residenza'); }, tipo: 'paese'},
  {chiave: 'dataNascita', get nome() { return i18next.t('Data di nascita'); }, tipo: 'date'},
  {chiave: 'codicePromo', get nome() { return i18next.t('Codice promozione'); }, tipo: 'text'},
  {chiave: 'codiceInvito', get nome() { return i18next.t('Codice invito'); }, tipo: 'text'},
];

/* I paesi piu' probabili in cima; l'elenco intero lo dara' il traduttore
   (DA COLLEGARE). Il valore e' il codice a due lettere, il nome si scrive
   nella lingua di chi guarda (nomePaese); quello qui e' il ripiego italiano. */
const PAESI = [
  ['IT', 'Italia'], ['BR', 'Brasile'], ['AR', 'Argentina'], ['MX', 'Messico'], ['ES', 'Spagna'],
  ['GB', 'Regno Unito'], ['DE', 'Germania'], ['FR', 'Francia'], ['TR', 'Turchia'], ['MY', 'Malesia'],
  ['ID', 'Indonesia'], ['TH', 'Thailandia'], ['PH', 'Filippine'], ['VN', 'Vietnam'], ['IN', 'India'],
  ['NG', 'Nigeria'], ['ZA', 'Sudafrica'], ['US', 'Stati Uniti'], ['CA', 'Canada'], ['AU', 'Australia'],
];

/** Il nome del paese nella lingua di chi guarda (Intl lo sa per tutte), o il nome italiano se il browser non lo sa. */
function nomePaese(codice, italiano) {
  try {
    return new Intl.DisplayNames([lingua()], {type: 'region'}).of(codice) || italiano;
  } catch (e) {
    return italiano;
  }
}

export function regoleRegistrazione() {
  const s = skinCorrente() || {};
  const r = s.registrazione || null;
  return {
    skinId: s.id || '',
    nomeSkin: s.name || '',
    conosciuta: !!r,
    aperta: !!(r && r.aperta),
    canali: (r && r.canali) || {},
    campi: (r && r.campi) || {},
    // I dati chiesti dei metodi con tabella propria (oggi «telegram»), gia' completi.
    // La registrazione classica usa sempre `campi`.
    campiPerMetodo: (r && r.campiPerMetodo) || {},
    perAttivare: (r && r.perAttivare) || 'uno',
    ore: (r && r.durataGettoneOre) || 24,
    // «Registrati con Telegram»: il Client ID del bot della skin (pubblico). Vuoto = niente pulsante.
    telegramClientId: (r && r.telegramClientId) || '',
  };
}

/* La libreria ufficiale di Telegram («Log in with Telegram») la carica e la
   tiene pronta services/telegramAccesso: la finestra di conferma restituisce
   un id_token che user-management verifica. */
const TELEGRAM_VUOTO = {idToken: '', id: '', nome: '', username: '', telefono: '', nomeUtenteLibero: false};

/* Il nome utente suggerito da Telegram: lettere, numeri e _ , almeno 3 caratteri. */
function nomeUtenteDa(username) {
  const v = String(username || '').replace(/[^A-Za-z0-9_]/g, '');
  return v.length >= 3 ? v : '';
}

/* Lo stato di un dato. `metodo` null = la registrazione classica, invariata;
   «telegram» = la tabella propria di Telegram (telefono sempre obbligatorio). */
function statoCampo(regole, chiave, metodo) {
  const suoi = metodo && regole.campiPerMetodo && regole.campiPerMetodo[metodo];
  const v = (suoi && suoi[chiave]) || regole.campi[chiave];
  return v || (chiave === 'email' ? 'obbligatorio' : 'facoltativo');
}

export default function Registrati({ userService, nelFoglio, onAccedi, onChiudi, ricerca, telegramIniziale, avvisoTelegram }) {
  const navigate = useNavigate();
  const inFinestra = typeof onChiudi === 'function';
  // la query da cui leggere: quella passata dalla finestra, altrimenti quella della pagina
  const query = ricerca !== undefined && ricerca !== null ? ricerca : (typeof window !== 'undefined' ? window.location.search : '');
  const regole = regoleRegistrazione();
  // «Telegram» non e' piu' una scelta di «Conferma con» (il vecchio flusso col bot e
  // «Avvia»): ha il suo pulsante «Registrati con Telegram». Restano Email e SMS.
  const canali = Object.keys(regole.canali).filter((c) => c !== 'telegram');
  const [dati, setDati] = React.useState({
    username: '', password: '', confirmPassword: '', email: '', telefono: '', paese: '',
    dataNascita: '', promoCode: '', referralCode: '', acceptTerms: false,
    canale: canali.includes('email') ? 'email' : (canali[0] || 'email'),
  });
  const [vedi, setVedi] = React.useState(false);
  /* «REGISTRATI CON TELEGRAM»: idToken e' quello che Telegram ha dato al sito;
     user-management lo verifica (firma, emittente, destinatario, scadenza). */
  const [telegram, setTelegram] = React.useState(TELEGRAM_VUOTO);
  const [telegramPronto, setTelegramPronto] = React.useState(false);
  const [erroreTelegram, setErroreTelegram] = React.useState('');
  // Il metodo con cui si sta registrando: con Telegram collegato valgono i SUOI dati
  // (telefono fisso, da Telegram); senza, la registrazione classica com'e'.
  const metodo = telegram.idToken ? 'telegram' : null;
  const [errori, setErrori] = React.useState({});
  const [erroreServizio, setErroreServizio] = React.useState('');
  const [invio, setInvio] = React.useState(false);
  /* IL CODICE INVITO: arriva dal link (?invito=…) o si scrive; la piattaforma
     dice subito se vale e da chi si viene invitati, e lo ricontrolla quando
     si apre il conto. {stato: 'vuoto'|'cerco'|'valido'|'non-valido'|'fermo', invitante, testo} */
  const [invito, setInvito] = React.useState({stato: 'vuoto'});
  /* Il conto e' appena stato aperto da qui: l'accesso automatico che segue non
     deve far scattare la guardia come se fosse un utente gia' dentro. */
  const appenaAperto = React.useRef(false);

  const vaiAdAccedi = () => (typeof onAccedi === 'function' ? onAccedi() : navigate('/login'));

  /* LA GUARDIA: chi e' gia' dentro non vede questa pagina, va alla home. */
  React.useEffect(() => {
    if (!userService || typeof userService.isLoggedIn !== 'function') return undefined;
    const sub = userService.isLoggedIn().subscribe((dentro) => {
      if (dentro && !appenaAperto.current) { if (inFinestra) onChiudi(); else navigate('/', {replace: true}); }
    });
    return () => sub.unsubscribe();
  }, [userService, navigate, inFinestra, onChiudi]);
  const codiceNascosto = statoCampo(regole, 'codiceInvito', metodo) === 'nascosto';
  /* SENZA CONFERMA: la skin ha scelto «niente: attivo subito» o «lo attiva un
     operatore» (Skin › Registrazione): nessun canale, nessun link ne' codice. */
  const senzaConferma = regole.perAttivare === 'niente' || regole.perAttivare === 'operatore'
    // con Telegram collegato l'account (e il telefono) e' provato: basta a «uno» e «telefono»
    || (!!telegram.idToken && (regole.perAttivare === 'uno' || regole.perAttivare === 'telefono'));

  /* La libreria e il nonce si tengono pronti (services/telegramAccesso): la finestra di Telegram deve
     aprirsi nel gesto dell'utente o il browser la blocca. */
  React.useEffect(() => {
    if (!regole.telegramClientId) return undefined;
    setTelegramPronto(true);
    return mantieProntoTelegram();
  }, [regole.telegramClientId]);

  /* Quello che Telegram ha detto di chi e' (dalla finestra o dal ritorno automatico): compila il modulo. */
  const telegramCollegato = (idToken, u) => {
    const tel = u.phone_number ? (String(u.phone_number).startsWith('+') ? String(u.phone_number) : `+${u.phone_number}`) : '';
    const nomeDaTelegram = nomeUtenteDa(u.preferred_username);
    setTelegram({idToken, id: String(u.id || u.sub || ''), nome: u.name || '', username: nomeDaTelegram, telefono: tel, nomeUtenteLibero: false});
    // LA @ DI TELEGRAM E' IL NOME UTENTE, bloccata: cosi' chi un domani entra senza
    // Telegram sa gia' come si chiama. Senza @, lo scrive il cliente (obbligatorio).
    // Il telefono: scritto e bloccato se Telegram lo passa, altrimenti come dice la tabella.
    setDati((d) => ({
      ...d,
      telefono: tel || d.telefono,
      username: nomeDaTelegram || d.username,
    }));
  };

  /* ACCEDI CON TELEGRAM, da qui. E' lo stesso pulsante di «Accedi»: chi ha gia' un
     conto con quel Telegram ENTRA (la finestra si chiude da sola: la guardia qui
     sopra vede l'accesso); chi non ce l'ha si ritrova il modulo compilato con i
     suoi dati, senza avvisi. Il nonce lo consuma l'accesso; la registrazione non
     ne ha bisogno. */
  const dopoTelegram = async (idToken, nonce, u) => {
    if (nonce && userService && typeof userService.doLoginTelegram === 'function') {
      const e = await userService.doLoginTelegram(idToken, nonce);
      if (e && e.esito === 'dentro') return;
      if (e && e.esito === 'errore') { setErroreTelegram(e.messaggio || i18next.t('Accesso con Telegram non riuscito')); return; }
    }
    telegramCollegato(idToken, u || {});
  };

  /* ARRIVATI DA «ACCEDI» (o da /login) con un Telegram senza conto: il modulo si
     apre gia' compilato con quei dati, e l'avviso lo dice. */
  React.useEffect(() => {
    if (telegramIniziale && telegramIniziale.idToken) telegramCollegato(telegramIniziale.idToken, telegramIniziale.user || {});
    // una volta sola, all'apertura
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* IL RITORNO AUTOMATICO: la pagina si riapre con il codice di Telegram nell'indirizzo. */
  React.useEffect(() => {
    let vivo = true;
    ritornoTelegram(ricerca, 'registrazione').then((r) => {
      if (!vivo || !r || r.scopo !== 'registrazione') return;
      if (r.errore) { if (r.errore !== 'annullato') setErroreTelegram(r.errore); return; }
      return dopoTelegram(r.idToken, r.nonce, r.user);
    }).catch((e) => { if (vivo) setErroreTelegram(e.message || i18next.t('Telegram non ha risposto')); });
    return () => { vivo = false; };
    // una volta sola, all'apertura
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const collegaTelegram = async () => {
    setErroreTelegram('');
    if (ritornoAutomaticoTelegram()) {
      // niente finestra: il browser va da Telegram e Telegram lo riporta qui
      avviaRitornoTelegram('registrazione').catch((e) => setErroreTelegram(e.message || i18next.t('Non riesco ad aprire Telegram')));
      return;
    }
    try {
      const {idToken, nonce, user} = await confermaConTelegram();
      await dopoTelegram(idToken, nonce, user);
    } catch (x) {
      if (!(x && x.annullato)) setErroreTelegram((x && x.message) || i18next.t('Non riesco ad aprire Telegram'));
    } finally {
      // il nonce e' usa e getta: se ne prepara un altro per il prossimo clic
      preparaTelegram().catch(() => { /* si riprova al clic */ });
    }
  };

  React.useEffect(() => {
    let dalLink = '';
    try { dalLink = new URLSearchParams(query).get('invito') || ''; } catch (x) { /* senza finestra */ }
    dalLink = dalLink.trim().toUpperCase();
    if (!dalLink) return;
    if (codiceNascosto) {
      setInvito({stato: 'fermo', testo: i18next.t('Questo sito non usa codici invito: il link che hai aperto non vale qui')});
      return;
    }
    setDati((d) => ({...d, referralCode: dalLink}));
    controllaInvito(dalLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const controllaInvito = async (codice) => {
    const c = String(codice || '').trim().toUpperCase();
    if (!c) { setInvito({stato: 'vuoto'}); return; }
    setInvito({stato: 'cerco'});
    const r = await cercaInvito(c);
    if (r.valido) setInvito({stato: 'valido', invitante: r.invitante || ''});
    else if (r.code === 'STRUTTURA_NON_RISPONDE') setInvito({stato: 'fermo', testo: r.message});
    else setInvito({stato: 'non-valido', testo: i18next.t('Codice invito non valido: controllalo o lascialo vuoto')});
  };

  const scrivi = (k) => (e) => setDati((d) => ({...d, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value}));

  /* Quello che si vede subito, con le parole della skin; il servizio ricontrolla. */
  const controlla = () => {
    const e = {};
    if (!dati.username.trim()) e.username = i18next.t('Scegli un nome utente');
    else if (dati.username.trim().length < 3) e.username = 'Almeno 3 caratteri';
    if (!/^(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])\S{8,}$/.test(dati.password)) {
      e.password = i18next.t('Almeno 8 caratteri, una maiuscola, un numero e un simbolo');
    }
    if (dati.password !== dati.confirmPassword) e.confirmPassword = i18next.t('Le due password non sono uguali');
    for (const c of CAMPI) {
      const st = statoCampo(regole, c.chiave, metodo);
      const v = String(dati[c.chiave === 'codicePromo' ? 'promoCode' : c.chiave === 'codiceInvito' ? 'referralCode' : c.chiave] || '').trim();
      if (st === 'obbligatorio' && !v) e[c.chiave] = i18next.t('Da compilare');
    }
    // senza conferma (attivo subito, o lo attiva un operatore) nessun canale pretende il suo dato
    if (!senzaConferma && dati.canale === 'email' && !dati.email.trim()) e.email = i18next.t('Serve per la conferma via email');
    if (!senzaConferma && dati.canale === 'sms' && !dati.telefono.trim()) e.telefono = i18next.t('Serve per la conferma via SMS');
    if (dati.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dati.email.trim())) e.email = i18next.t('Non e’ un indirizzo email');
    // con Telegram il telefono lo da' Telegram se c'e'; se no vale la tabella del metodo (gia' nel giro dei CAMPI)
    if (!dati.acceptTerms) e.acceptTerms = i18next.t('Devi accettare il regolamento');
    return e;
  };

  const invia = async (ev) => {
    ev.preventDefault();
    setErroreServizio('');
    const e = controlla();
    setErrori(e);
    if (Object.keys(e).length) return;
    setInvio(true);
    try {
      const corpo = {
        username: dati.username.trim(), password: dati.password, confirmPassword: dati.confirmPassword,
        // senza conferma il canale non serve: non si manda, cosi' il servizio non lo pretende acceso
        acceptTerms: true, canale: senzaConferma ? undefined : dati.canale,
      };
      if (telegram.idToken) corpo.telegramIdToken = telegram.idToken;
      for (const c of CAMPI) {
        if (statoCampo(regole, c.chiave, metodo) === 'nascosto') continue;
        const k = c.chiave === 'codicePromo' ? 'promoCode' : c.chiave === 'codiceInvito' ? 'referralCode' : c.chiave;
        if (String(dati[k] || '').trim()) corpo[k] = String(dati[k]).trim();
      }
      const utente = await registra(corpo);
      /* ATTIVO SUBITO (skin su «niente»): si entra da soli con il nome utente e
         la password appena scelti e si va alla home, senza altri clic. Con
         «operatore» o con una conferma da fare il conto non puo' entrare: si
         va alla pagina che lo dice. */
      if ((utente.attivazione || '') === 'subito' && userService && typeof userService.doLogin === 'function') {
        appenaAperto.current = true;
        try { window.sessionStorage.removeItem('lvc.contoInAttesa'); } catch (x) { /* navigazione privata */ }
        try {
          await userService.doLogin(dati.username.trim(), dati.password);
        } catch (x) { /* l'esito si legge da isLoggedIn, qui sotto */ }
        if (inFinestra) onChiudi();
        navigate('/', {replace: true});
        setInvio(false);
        return;
      }
      try {
        window.sessionStorage.setItem('lvc.contoInAttesa', JSON.stringify({
          username: utente.username || dati.username.trim(), email: dati.email.trim(), telefono: dati.telefono.trim(),
          canale: dati.canale, skinId: regole.skinId, ore: regole.ore, quando: Date.now(),
          // come si attiva: lo dice user-management («subito», «operatore», «conferma»)
          attivazione: utente.attivazione || (senzaConferma ? regole.perAttivare === 'niente' ? 'subito' : 'operatore' : 'conferma'),
        }));
      } catch (x) { /* navigazione privata */ }
      if (inFinestra) onChiudi();
      navigate('/activate-account');
    } catch (err) {
      // un rifiuto sul codice invito si scrive sotto il campo, con il suo codice
      if (err.code && String(err.code).startsWith('CODICE_INVITO') || err.code === 'LIVELLO_INVITO_MANCANTE') {
        setErrori((e2) => ({...e2, codiceInvito: `${err.code} · ${err.messaggio || err.message}`}));
        setInvito({stato: 'non-valido', testo: err.messaggio || err.message});
      } else {
        // una conferma di Telegram non valida o scaduta si rifa': si toglie e si ripropone il pulsante
        if (err.code && String(err.code).startsWith('TELEGRAM_')) setTelegram(TELEGRAM_VUOTO);
        // la @ di Telegram e' gia' di un altro conto: il campo si sblocca e il cliente ne sceglie un'altra
        if (err.codice === 409 || /gia' in uso|già in uso/i.test(String(err.messaggio || ''))) {
          if (telegram.username && !telegram.nomeUtenteLibero) {
            setTelegram((t) => ({...t, nomeUtenteLibero: true}));
            setErrori((e2) => ({...e2, username: i18next.t('Il nome utente della tua @ di Telegram è già usato: scegline un altro')}));
          }
        }
        setErroreServizio(`Errore ${err.codice || ''} - ${err.messaggio || err.message}`.replace('  ', ' '));
      }
    }
    setInvio(false);
  };

  if (!regole.conosciuta) {
    return (
      <section className={'reg-pagina'}>
        <h1>{i18next.t('Apri il tuo conto')}</h1>
        {inAnteprima()
          ? <Nota tono={'errore'}>{i18next.t('Errore 503 - la skin non ha ancora detto come si fa la registrazione (tenants: campo «registrazione» mancante). Si imposta dal backoffice, Skin › Registrazione.')}</Nota>
          : <Nota>{i18next.t(CHIUSA_PER_ORA)}</Nota>}
      </section>
    );
  }
  if (!regole.aperta) {
    return (
      <section className={'reg-pagina'}>
        <h1>{i18next.t('Apri il tuo conto')}</h1>
        <Nota>{i18next.t('La registrazione online e\' chiusa per')}{' '}{regole.nomeSkin || 'questo sito'}{i18next.t(': i conti si aprono tramite agenzia. Se hai gia\' un conto,')}{' '}<button type={'button'} className={'reg-link'} onClick={vaiAdAccedi}>{i18next.t('accedi')}</button>.</Nota>
      </section>
    );
  }

  const campo = (c) => {
    // CON TELEGRAM: se Telegram ha passato il numero e' gia' scritto e bloccato (verificato);
    // se no vale la tabella del metodo (di serie facoltativo), e un numero scritto non si cambia piu'.
    if (c.chiave === 'telefono' && metodo === 'telegram' && telegram.telefono) {
      return (
        <div className={'reg-campo'} key={'telefono'}>
          <label htmlFor={'reg-telefono'}>{c.nome}</label>
          <input id={'reg-telefono'} type={'tel'} value={telegram.telefono} readOnly aria-readonly={'true'}/>
          <small>{i18next.t('verificato da Telegram: non si cambia')}</small>
        </div>
      );
    }
    // IL TELEGRAM ID: gia' scritto, non si tocca. E' quello che collega il conto all'account.
    if (c.chiave === 'telegramId') {
      if (metodo !== 'telegram') return null;
      return (
        <div className={'reg-campo'} key={'telegramId'}>
          <label htmlFor={'reg-telegram-id'}>{i18next.t('Telegram ID')}</label>
          <input id={'reg-telegram-id'} type={'text'} value={telegram.id} readOnly aria-readonly={'true'}/>
          <small>{i18next.t('l’identificativo del tuo account Telegram: collega il conto, non si cambia')}</small>
        </div>
      );
    }
    const st = statoCampo(regole, c.chiave, metodo);
    if (st === 'nascosto') return null;
    const k = c.chiave === 'codicePromo' ? 'promoCode' : c.chiave === 'codiceInvito' ? 'referralCode' : c.chiave;
    const obbl = st === 'obbligatorio' || (!senzaConferma && c.chiave === 'email' && dati.canale === 'email') || (!senzaConferma && c.chiave === 'telefono' && dati.canale === 'sms');
    return (
      <div className={'reg-campo' + (errori[c.chiave] ? ' errore' : '')} key={c.chiave}>
        <label htmlFor={'reg-' + c.chiave}>{c.nome}{obbl ? ' *' : ''}</label>
        {c.tipo === 'paese' ? (
          <select id={'reg-' + c.chiave} value={dati.paese} onChange={scrivi('paese')} autoComplete={'country'}>
            <option value={''}>{i18next.t('Scegli')}</option>
            {PAESI.map(([v, n]) => <option key={v} value={v}>{nomePaese(v, n)}</option>)}
          </select>
        ) : (
          <input id={'reg-' + c.chiave} type={c.tipo} value={dati[k]}
            onChange={c.chiave === 'codiceInvito'
              ? (e) => { const v = e.target.value.toUpperCase(); setDati((d) => ({...d, referralCode: v})); setErrori((e2) => ({...e2, codiceInvito: undefined})); setInvito({stato: 'vuoto'}); }
              : scrivi(k)}
            onBlur={c.chiave === 'codiceInvito' ? () => controllaInvito(dati.referralCode) : undefined}
            placeholder={c.chiave === 'codiceInvito' ? i18next.t('se qualcuno ti ha invitato') : (c.aiuto || '')}
            autoComplete={c.auto || 'off'} inputMode={c.tipo === 'tel' ? 'tel' : undefined}/>
        )}
        {errori[c.chiave] ? <small>{errori[c.chiave]}</small>
          : c.chiave === 'codiceInvito' ? (
            invito.stato === 'valido' ? <small className={'reg-invito-ok'}>{i18next.t('Invitato da')}{' '}<b>{invito.invitante}</b>{' '}{i18next.t('· il tuo conto nasce nella sua rete')}</small>
            : invito.stato === 'cerco' ? <small>{i18next.t('controllo il codice…')}</small>
            : invito.stato === 'non-valido' || invito.stato === 'fermo' ? <small className={'reg-invito-no'}>{invito.testo}</small>
            : <small>{i18next.t('Senza codice il conto nasce direttamente nella skin.')}</small>
          )
          : (c.chiave === 'telefono' && !obbl ? <small>{i18next.t('facoltativo: serve solo se confermi via SMS o Telegram')}</small> : null)}
      </div>
    );
  };

  return (
    <section className={'reg-pagina' + (nelFoglio ? ' nel-foglio' : '')} aria-label={i18next.t('Registrazione')}>
      {!nelFoglio && <h1>{i18next.t('Apri il tuo conto')}</h1>}
      <p className={'reg-sotto'}>{i18next.t('Due minuti. I campi con * sono obbligatori.')}</p>

      {regole.telegramClientId && (
        <div className={'reg-conferma'}>
          {telegram.idToken ? (
            <>
              {avvisoTelegram && <Nota>{i18next.t('Questo account Telegram non ha ancora un conto: completa la registrazione, i tuoi dati sono già compilati.')}</Nota>}
              <div className={'reg-etichetta'}>{i18next.t('Telegram collegato')}</div>
              <p className={'reg-sotto'}>
                <b>{telegram.nome || telegram.username || i18next.t('Il tuo account')}</b>
                {telegram.username ? ` (@${telegram.username})` : ''}
                {telegram.id ? ` · ID ${telegram.id}` : ''}
                {telegram.telefono ? ` · ${telegram.telefono}` : ''}
              </p>
              <button type={'button'} className={'reg-link'} onClick={() => { setTelegram(TELEGRAM_VUOTO); setDati((d) => ({...d, telefono: ''})); }}>{i18next.t('usa un altro account')}</button>
            </>
          ) : (
            <>
              <button type={'button'} className={'reg-apri'} onClick={collegaTelegram} disabled={!telegramPronto}>
                {i18next.t('ACCEDI CON TELEGRAM')}
              </button>
              <small className={'reg-nota'}>{i18next.t('Si apre Telegram: se hai già un conto entri, altrimenti i tuoi dati si compilano da soli.')}</small>
            </>
          )}
          {erroreTelegram && <Nota tono={'errore'}>{erroreTelegram}</Nota>}
        </div>
      )}

      <form onSubmit={invia} noValidate>
        {invito.stato === 'fermo' && codiceNascosto && <Nota tono={'errore'}>{invito.testo}</Nota>}
        <div className={'reg-campo' + (errori.username ? ' errore' : '')}>
          <label htmlFor={'reg-username'}>{i18next.t('Nome utente *')}</label>
          <input id={'reg-username'} type={'text'} value={dati.username} onChange={scrivi('username')} autoComplete={'username'}
            readOnly={!!(telegram.idToken && telegram.username && !telegram.nomeUtenteLibero)}/>
          {errori.username ? <small>{errori.username}</small>
            : telegram.idToken && telegram.username && !telegram.nomeUtenteLibero ? <small>{i18next.t('la tua @ di Telegram: non si cambia')}</small>
            : telegram.idToken ? <small>{i18next.t('su Telegram non hai una @: scegli il nome con cui entrare anche senza Telegram')}</small> : null}
        </div>

        {campo({chiave: 'telegramId'})}
        {campo(CAMPI[0])}
        {campo(CAMPI[2])}
        {campo(CAMPI[1])}
        {campo(CAMPI[3])}

        <div className={'reg-campo' + (errori.password ? ' errore' : '')}>
          <label htmlFor={'reg-password'}>{i18next.t('Password *')}</label>
          <div className={'reg-con-occhio'}>
            <input id={'reg-password'} type={vedi ? 'text' : 'password'} value={dati.password} onChange={scrivi('password')}
              placeholder={i18next.t('8+ caratteri, una maiuscola, un numero, un simbolo')} autoComplete={'new-password'}/>
            <button type={'button'} className={'reg-occhio'} aria-pressed={vedi} onClick={() => setVedi((v) => !v)}>{vedi ? 'nascondi' : 'mostra'}</button>
          </div>
          {errori.password && <small>{errori.password}</small>}
        </div>
        <div className={'reg-campo' + (errori.confirmPassword ? ' errore' : '')}>
          <label htmlFor={'reg-conferma'}>{i18next.t('Ripeti la password *')}</label>
          <input id={'reg-conferma'} type={vedi ? 'text' : 'password'} value={dati.confirmPassword} onChange={scrivi('confirmPassword')} autoComplete={'new-password'}/>
          {errori.confirmPassword && <small>{errori.confirmPassword}</small>}
        </div>

        {campo(CAMPI[4])}
        {campo(CAMPI[5])}

        <label className={'reg-termini' + (errori.acceptTerms ? ' errore' : '')}>
          <input type={'checkbox'} checked={dati.acceptTerms} onChange={scrivi('acceptTerms')}/>
          <span>{i18next.t('Accetto i')}{' '}<button type={'button'} className={'reg-link'} onClick={() => navigate('/regolamento')}>{i18next.t('termini')}</button>{' '}{i18next.t('e ho piu\' di 18 anni *')}</span>
        </label>

        {!senzaConferma && canali.length > 0 && (
          <div className={'reg-conferma'}>
            <div className={'reg-etichetta'}>{i18next.t('Conferma con')}</div>
            <div className={'reg-canali'} role={'radiogroup'} aria-label={i18next.t('Canale di conferma')}>
              {canali.map((c) => (
                <button type={'button'} key={c} role={'radio'} aria-checked={dati.canale === c}
                  className={'lvc-cella lvc-quota' + (dati.canale === c ? ' lvc-scelta' : '')}
                  onClick={() => setDati((d) => ({...d, canale: c}))}>
                  {NOMI_CANALE[c] ? i18next.t(NOMI_CANALE[c]) : c}
                </button>
              ))}
            </div>
            <small className={'reg-nota'}>
              {dati.canale === 'email' && i18next.t('Ti mandiamo un link che vale {{ore}} ore.', {ore: regole.ore})}
              {dati.canale === 'sms' && i18next.t('Ti mandiamo un codice via SMS che vale {{ore}} ore.', {ore: regole.ore})}
              {' '}{i18next.t('Finche\' non confermi, il conto resta in attesa.')}
            </small>
          </div>
        )}
        {!senzaConferma && canali.length === 0 && (regole.telegramClientId
          ? <Nota>{i18next.t('Per questo sito il conto si apre con «Accedi con Telegram», qui sopra.')}</Nota>
          : inAnteprima()
            ? <Nota tono={'errore'}>{i18next.t('Errore 503 - la skin ha la registrazione aperta ma nessun canale di conferma acceso. Si sistema dal backoffice, Skin › Registrazione.')}</Nota>
            : <Nota>{i18next.t(CHIUSA_PER_ORA)}</Nota>
        )}
        {senzaConferma && (
          <small className={'reg-nota'}>
            {regole.perAttivare === 'niente'
              ? i18next.t('Nessuna conferma: appena apri il conto puoi accedere.')
              : i18next.t('Il conto viene registrato e resta in attesa: lo attiva un operatore, poi potrai accedere.')}
          </small>
        )}

        {erroreServizio && <Nota tono={'errore'}>{erroreServizio}</Nota>}

        <button type={'submit'} className={'reg-apri'} disabled={invio || (!senzaConferma && canali.length === 0)}>
          {invio ? i18next.t('Un attimo…') : i18next.t('APRI IL CONTO')}
        </button>
        <p className={'reg-sotto centro'}>{i18next.t('Hai gia\' un conto?')}{' '}<button type={'button'} className={'reg-link'} onClick={vaiAdAccedi}>{i18next.t('Accedi')}</button></p>
      </form>
    </section>
  );
}
